import { CheckboxOptionType, Input, Modal, Radio, Select, Space, Spin, Switch } from 'antd'
import React, { FC, useContext, useState } from 'react'
import { getItemTypeComboOptions } from '../func/type-options'
import { translate } from '../../../core'
import { ItemPageByCustomFieldRequest } from '../type/item-dto'
import { SortDirection } from '../../../core/component/Search/SortDirection'
import { Item, ItemCategoryLevel, ItemEntry, ItemType } from '../type'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GetVendorsForLookup } from '../../contact/graphql/contact.queries.graphql'
import { GetItemCategoryList } from '../graphql/item-category.graphql'
import { GetItemPageByCustomField } from '../graphql/item.graphql'
import { ErrorMessage, ShowIf } from '../../../core/component'
import { SearchProps } from '../../../core/component/Search'
import { PageContextValue } from '../../../core/type'
import { ItemPageContext } from './ItemPageContext'
import { ItemFindRequest, ItemShowOptions } from '../type/item-query'
import { showItemOptionsMapper, ShowItemOptionsSelect } from '../func/showItemOptionsList'

const style: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}

type RequestByField = Record<string, ItemPageByCustomFieldRequest['condition']>

const BOOLEAN_FIELDS = ['showOnWeb', 'inStock']

const booleanFieldsDefault = () => BOOLEAN_FIELDS.reduce(
  (defaults, field) => ({
    ...defaults,
    [field]: { value: 'false' } as ItemPageByCustomFieldRequest['condition'],
  }),
  {} as RequestByField,
)

const SearchItemContainer: FC<{ disabled: boolean, children: React.ReactNode }> = ({ disabled, children }) => (
  <Spin spinning={disabled} indicator={<></>}>
    <Space>{children}</Space>
  </Spin>
)

export const ItemAdvancedSearch: FC<SearchProps> = ({ open, closeMe }) => {
  const { fetch } = useContext<PageContextValue<Item, ItemEntry>>(ItemPageContext)
  const [selectedField, setSelectedField] = useState<string>()
  const [type, setType] = useState<ItemType[]>([])
  const [request, setRequest] = useState<RequestByField>(booleanFieldsDefault)
  const [vendorIdSearch, setVendorIdSearch] = useState<string>()
  const [categorySearch, setCategorySearch] = useState<string>()
  const [subcategorySearch, setSubcategorySearch] = useState<string>()
  const [subcategory2Search, setSubcategory2Search] = useState<string>()
  const [showStatus, setShowStatus] = useState<ItemShowOptions | undefined>(ItemShowOptions.NoneArchived)

  const setConditionValue = (field: string, propertyName: string, value: string | boolean) => {
    setRequest(pre => ({
      ...pre,
      [field]: {
        ...pre?.[field] ?? {} as any,
        [propertyName]: value,
      }
    }))
  }

  const { data: vendorData, loading: vendorLoading } = useQuery(GetVendorsForLookup, {
    variables: {
      request: {
        take: 20,
        skip: 0,
        contactId: vendorIdSearch,
      }
    }
  })

  const { data: categoryData, loading: categoryLoading } = useQuery(GetItemCategoryList, {
    variables: {
      request: {
        take: 20,
        skip: 0,
        title: categorySearch,
        level: ItemCategoryLevel.Category,
      }
    }
  })

  const { data: subcategoryData, loading: subcategoryLoading } = useQuery(GetItemCategoryList, {
    variables: {
      request: {
        take: 20,
        skip: 0,
        title: subcategorySearch,
        level: ItemCategoryLevel.Subcategory,
      }
    }
  })

  const { data: subcategory2Data, loading: subcategory2Loading } = useQuery(GetItemCategoryList, {
    variables: {
      request: {
        take: 20,
        skip: 0,
        title: subcategory2Search,
        level: ItemCategoryLevel.Subcategory2,
      }
    }
  })

  const [getPage, { loading }] = useLazyQuery(GetItemPageByCustomField)
  const [error, setError] = useState<string>()

  const options: CheckboxOptionType[] = [
    {
      value: 'vendor',
      label: <SearchItemContainer disabled={selectedField !== 'vendor'}>
        <div style={{ width: 150 }}>
          <label>{translate('vendor')}</label>
        </div>
        <Select
          loading={vendorLoading}
          style={{ width: 300 }}
          options={(vendorData?.contactsFind?.data ?? []).map(v => ({
            label: v.contactId,
            value: v.contactId,
          }))}
          value={request?.vendor?.value}
          onChange={value => setConditionValue('vendor', 'value', value)}
          onSearch={setVendorIdSearch}
          filterOption={false}
          showSearch
          allowClear
        />
        <SortDirection
          value={request?.vendor?.sortOrder}
          onChange={value => setConditionValue('vendor', 'sortOrder', value)}
        />
      </SearchItemContainer>
    },
    {
      value: 'style',
      label: <SearchItemContainer disabled={selectedField !== 'style'}>
        <div style={{ width: 150 }}>
          <label>{translate('style', '/', 'pattern')}</label>
        </div>
        <Input style={{ width: 300 }}
          value={request?.style?.value}
          onChange={e => setConditionValue('style', 'value', e.target.value)}
        />
        <SortDirection
          value={request?.style?.sortOrder}
          onChange={value => setConditionValue('style', 'sortOrder', value)}
        />
        <Switch
          value={request?.style?.exactMatch}
          onChange={value => setConditionValue('style', 'exactMatch', value)}
        />
        <span className='fs-6'>{translate('exact_match')}</span>
      </SearchItemContainer>
    },
    {
      value: 'category',
      label: <SearchItemContainer disabled={selectedField !== 'category'}>
        <div style={{ width: 150 }}>
          <label>{translate('category')}</label>
        </div>
        <Select
          loading={categoryLoading}
          style={{ width: 300 }}
          options={(categoryData?.itemCategoryListFind?.data ?? []).map(v => ({
            label: v.title,
            value: v.title,
          }))}
          value={request?.category?.value}
          onChange={value => setConditionValue('category', 'value', value)}
          onSearch={setCategorySearch}
          filterOption={false}
          showSearch
          allowClear
        />
        <SortDirection
          value={request?.category?.sortOrder}
          onChange={value => setConditionValue('category', 'sortOrder', value)}
        />
      </SearchItemContainer>
    },
    {
      value: 'subcategory',
      label: <SearchItemContainer disabled={selectedField !== 'subcategory'}>
        <div style={{ width: 150 }}>
          <label>{translate('subcategory')}</label>
        </div>
        <Select
          loading={subcategoryLoading}
          style={{ width: 300 }}
          options={(subcategoryData?.itemCategoryListFind?.data ?? []).map(v => ({
            label: v.title,
            value: v.title,
          }))}
          value={request?.subcategory?.value}
          onChange={value => setConditionValue('subcategory', 'value', value)}
          onSearch={setSubcategorySearch}
          filterOption={false}
          showSearch
          allowClear
        />
        <SortDirection
          value={request?.subcategory?.sortOrder}
          onChange={value => setConditionValue('subcategory', 'sortOrder', value)}
        />
      </SearchItemContainer>
    },
    {
      value: 'subcategory2',
      label: <SearchItemContainer disabled={selectedField !== 'subcategory2'}>
        <div style={{ width: 150 }}>
          <label>{translate('subcategory2')}</label>
        </div>
        <Select
          loading={subcategory2Loading}
          style={{ width: 300 }}
          options={(subcategory2Data?.itemCategoryListFind?.data ?? []).map(v => ({
            label: v.title,
            value: v.title,
          }))}
          value={request?.subcategory2?.value}
          onChange={value => setConditionValue('subcategory2', 'value', value)}
          onSearch={setSubcategory2Search}
          filterOption={false}
          showSearch
          allowClear
        />
        <SortDirection
          value={request?.subcategory2?.sortOrder}
          onChange={value => setConditionValue('subcategory2', 'sortOrder', value)}
        />
      </SearchItemContainer>
    },
    {
      value: 'patternNumber',
      label: <SearchItemContainer disabled={selectedField !== 'patternNumber'}>
        <div style={{ width: 150 }}>
          <label>{translate('pattern_number_abbr')}</label>
        </div>
        <Input style={{ width: 300 }}
          value={request?.patternNumber?.value}
          onChange={e => setConditionValue('patternNumber', 'value', e.target.value)}
        />
        <SortDirection
          value={request?.patternNumber?.sortOrder}
          onChange={value => setConditionValue('patternNumber', 'sortOrder', value)}
        />
        <Switch
          value={request?.patternNumber?.exactMatch}
          onChange={value => setConditionValue('patternNumber', 'exactMatch', value)}
        />
        <span className='fs-6'>{translate('exact_match')}</span>
      </SearchItemContainer>
    },
    {
      value: 'showOnWeb',
      label: <SearchItemContainer disabled={selectedField !== 'showOnWeb'}>
        <div style={{ width: 150 }}>
          <label>{translate('show', 'on', 'web')}</label>
        </div>
        <Switch
          value={request?.showOnWeb?.value === 'true'}
          onChange={value => setConditionValue('showOnWeb', 'value', value.toString())}
        />
        <SortDirection
          value={request?.showOnWeb?.sortOrder}
          onChange={value => setConditionValue('showOnWeb', 'sortOrder', value)}
        />
      </SearchItemContainer>
    },
    {
      value: 'inStock',
      label: <SearchItemContainer disabled={selectedField !== 'inStock'}>
        <div style={{ width: 150 }}>
          <label>{translate('in_stock_only')}</label>
        </div>
        <Switch
          value={request?.inStock?.value === 'true'}
          onChange={value => setConditionValue('inStock', 'value', value.toString())}
        />
      </SearchItemContainer>
    },
  ]

  const handleOk = async () => {
    const condition = request?.[selectedField ?? ''] ?? {} as ItemPageByCustomFieldRequest['condition']

    condition.field = selectedField ?? ''

    if (typeof condition.exactMatch === 'undefined')
      condition.exactMatch = false

    if (typeof condition?.sortOrder === 'undefined')
      condition.sortOrder = 'asc'

    const result = await getPage({
      variables: {
        request: {
          type,
          archived: showItemOptionsMapper[showStatus ?? ItemShowOptions.NoneArchived].value,
          condition,
        },
      },
    })

    if (!result?.data?.itemPageByCustomField)
      return setError(translate('not_found'))

    const findRequest: ItemFindRequest = {
      types: type,
      archived: showItemOptionsMapper[showStatus ?? ItemShowOptions.NoneArchived].value,
      sort: [
        {
          field: condition.field,
          sortOrder: condition.sortOrder,
        },
      ],
    }

    setError(undefined)
    fetch!(findRequest, result.data.itemPageByCustomField)
    closeMe()
  }

  return (
    <Modal
      open={open}
      title={translate('advanced', 'search')}
      onCancel={closeMe}
      width='50%'
      onOk={handleOk}
      okButtonProps={{
        loading,
        disabled: !(type?.length && selectedField && request?.[selectedField]?.value),
      }}
    >
      <ShowIf
        condition={!!error}
        replacement={<></>}
      >
        <ErrorMessage
          title={translate('error')}
          message={error ?? ''}
        />
      </ShowIf>

      <Space>
        <label>{translate('type')}:</label>
        <Select
          value={type}
          style={{ width: 300 }}
          mode='multiple'
          options={getItemTypeComboOptions()}
          onChange={setType}
        />
      </Space>

      <Space
        className='ms-4'
      >
        <label>{translate('show')}:</label>
        <ShowItemOptionsSelect
          value={showStatus}
          onChange={setShowStatus}
        />
      </Space>

      <Radio.Group
        onChange={e => setSelectedField(e.target.value)}
        className='mt-4'
        style={style}
        options={options}
      />
    </Modal>
  )
}